import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getWebsiteLeads, updateWebsiteLead, deleteWebsiteLead } from '@/lib/api';
import { Globe, Mail, CalendarClock, MessageSquare, Trash2, RefreshCw, Loader2, ChevronDown, ChevronRight, CheckCircle2, Archive, Inbox, Home } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  contacted: 'bg-blue-50 text-blue-700 border-blue-200',
  archived: 'bg-muted text-muted-foreground border-border',
};

const STATUS_LABEL = { new: 'New', contacted: 'Contacted', archived: 'Archived' };

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateRange(a, b) {
  if (!a && !b) return '—';
  const fmt = (s) => s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  return `${fmt(a)} → ${fmt(b)}`;
}

export function WebsiteLeadsWindow({ open, onClose, adminPass }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [filter, setFilter] = useState('active'); // active | all | new | contacted | archived

  const fetchLeads = useCallback(async () => {
    if (!adminPass) return;
    setLoading(true);
    try {
      const data = await getWebsiteLeads(adminPass);
      setLeads(data || []);
    } catch {
      toast.error('Failed to load website leads');
    } finally {
      setLoading(false);
    }
  }, [adminPass]);

  useEffect(() => { if (open) fetchLeads(); }, [open, fetchLeads]);

  const toggleExpand = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const patchLead = async (id, patch) => {
    try {
      const updated = await updateWebsiteLead(id, { password: adminPass, ...patch });
      setLeads(list => list.map(l => l.id === id ? updated : l));
    } catch {
      toast.error('Failed to update lead');
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Permanently delete this lead from ${lead.name || 'unknown'}?`)) return;
    try {
      await deleteWebsiteLead(lead.id, adminPass);
      setLeads(list => list.filter(l => l.id !== lead.id));
      toast.success('Lead deleted');
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const visibleLeads = leads.filter(l => {
    if (filter === 'all') return true;
    if (filter === 'active') return l.status !== 'archived';
    return l.status === filter;
  });

  const counts = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    archived: leads.filter(l => l.status === 'archived').length,
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-[900px] h-[85vh] p-0 flex flex-col overflow-hidden" data-testid="website-leads-window">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            <div>
              <h2 className="text-[16px] font-semibold">Website Leads</h2>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Submissions from the Flex Living Miami public site
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs">
              <Badge variant="outline" className={`${STATUS_COLORS.new} border`}>{counts.new} new</Badge>
              <Badge variant="outline" className={`${STATUS_COLORS.contacted} border`}>{counts.contacted} contacted</Badge>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchLeads} title="Refresh" data-testid="website-leads-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="px-5 py-2 border-b flex-shrink-0 flex items-center gap-1 overflow-x-auto">
          {[
            { key: 'active',    label: `Active (${counts.total - counts.archived})` },
            { key: 'new',       label: `New (${counts.new})` },
            { key: 'contacted', label: `Contacted (${counts.contacted})` },
            { key: 'archived',  label: `Archived (${counts.archived})` },
            { key: 'all',       label: `All (${counts.total})` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex-shrink-0 ${
                filter === t.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
              data-testid={`website-leads-filter-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && leads.length === 0 ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : visibleLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <Inbox className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="font-medium">No {filter === 'all' ? '' : filter} leads yet</p>
              <p className="text-xs text-muted-foreground mt-1">Submissions from the public site will appear here.</p>
            </div>
          ) : (
            <div className="divide-y">
              {visibleLeads.map(lead => {
                const isOpen = expanded.has(lead.id);
                return (
                  <div key={lead.id} className="px-5 py-3" data-testid="website-lead-row">
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleExpand(lead.id)} className="mt-1 text-muted-foreground hover:text-foreground flex-shrink-0" data-testid="website-lead-expand">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="font-semibold text-sm truncate" data-testid="website-lead-name">{lead.name}</p>
                          {lead.listing_name && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 h-[18px] border-emerald-200 bg-emerald-50 text-emerald-700 gap-1"
                              data-testid="website-lead-listing-badge"
                              title={lead.listing_name}
                            >
                              <Home className="h-2.5 w-2.5" />
                              <span className="truncate max-w-[140px]">{lead.listing_name}</span>
                            </Badge>
                          )}
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-[18px] border ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`} data-testid="website-lead-status-badge">
                            {STATUS_LABEL[lead.status] || lead.status}
                          </Badge>
                          <span className="text-[11px] text-muted-foreground ml-auto">{formatWhen(lead.submitted_at)}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                          <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-primary" data-testid="website-lead-email">
                            <Mail className="h-3 w-3" />{lead.email}
                          </a>
                          <span className="inline-flex items-center gap-1">
                            <CalendarClock className="h-3 w-3" />{fmtDateRange(lead.move_in, lead.move_out)}
                          </span>
                          {lead.message && (
                            <span className="inline-flex items-center gap-1 truncate max-w-[280px]">
                              <MessageSquare className="h-3 w-3" />{lead.message.length > 60 ? lead.message.slice(0, 60) + '…' : lead.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Select value={lead.status} onValueChange={v => patchLead(lead.id, { status: v })}>
                          <SelectTrigger className="h-7 text-xs w-[115px]" data-testid="website-lead-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new"><span className="inline-flex items-center gap-1"><Inbox className="h-3 w-3" />New</span></SelectItem>
                            <SelectItem value="contacted"><span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Contacted</span></SelectItem>
                            <SelectItem value="archived"><span className="inline-flex items-center gap-1"><Archive className="h-3 w-3" />Archived</span></SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(lead)} title="Delete" data-testid="website-lead-delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="mt-3 ml-7 pl-3 border-l-2 border-border/60 space-y-3" data-testid="website-lead-detail">
                        {lead.message && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Full message</p>
                            <p className="text-sm whitespace-pre-wrap bg-muted/40 rounded-lg px-3 py-2">{lead.message}</p>
                          </div>
                        )}
                        <div>
                          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 block">Your notes</Label>
                          <textarea
                            key={lead.id}
                            defaultValue={lead.notes || ''}
                            onBlur={e => {
                              const next = e.target.value;
                              if (next !== (lead.notes || '')) patchLead(lead.id, { notes: next });
                            }}
                            placeholder="Private notes for yourself…"
                            rows={2}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
                            data-testid="website-lead-notes"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
